// src/components/DocumentPiP.js

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Renders children into a provided Picture-in-Picture window using a React Portal.
 * Designed for native Document Picture-in-Picture windows only.
 *
 * @param {object} props
 * @param {Window | null} props.pipWindow - The PiP window object returned by the useDocumentPiP hook.
 * @param {React.ReactNode} props.children - The content to render.
 */
const DocumentPiP = ({ pipWindow, children }) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!pipWindow) {
      setIsReady(false);
      return;
    }

    // Check if window is closed
    if (pipWindow.closed) {
      setIsReady(false);
      return;
    }

    // For Document PiP windows, check if body exists
    const checkReady = () => {
      if (pipWindow.closed) {
        setIsReady(false);
        return;
      }

      try {
        // Document PiP window - check for body
        if (
          pipWindow.document?.body &&
          pipWindow.document.readyState === 'complete'
        ) {
          setIsReady(true);
          return;
        }
      } catch (e) {
        // Cross-origin or other error
        // Error checking window readiness
      }

      // Keep checking
      setTimeout(checkReady, 50);
    };

    // Start checking
    checkReady();
  }, [pipWindow]);

  // Copy stylesheets to the PiP window and set background color
  useEffect(() => {
    if (pipWindow && isReady && pipWindow.document) {
      try {
        // Set background color for Document PiP windows
        const html = pipWindow.document.documentElement;
        const body = pipWindow.document.body;
        if (html) {
          html.style.margin = '0';
          html.style.padding = '0';
          html.style.width = '100%';
          html.style.height = '100%';
          html.style.overflow = 'hidden';
          html.style.backgroundColor = '#3c4043';
          html.style.color = '#ffffff';
        }
        if (body) {
          body.style.margin = '0';
          body.style.padding = '0';
          body.style.width = '100%';
          body.style.height = '100%';
          body.style.overflow = 'hidden';
          body.style.backgroundColor = '#3c4043';
          body.style.color = '#ffffff';
        }

        Array.from(document.styleSheets).forEach(styleSheet => {
          try {
            const cssRules = Array.from(styleSheet.cssRules)
              .map(rule => rule.cssText)
              .join('');
            const style = pipWindow.document.createElement('style');
            style.textContent = cssRules;
            pipWindow.document.head.appendChild(style);
          } catch (e) {
            if (styleSheet.href) {
              const link = pipWindow.document.createElement('link');
              link.rel = 'stylesheet';
              link.href = styleSheet.href;
              pipWindow.document.head.appendChild(link);
            }
          }
        });
      } catch (error) {
        // Error copying stylesheets to PiP window
      }
    }
  }, [pipWindow, isReady]);

  // If the window doesn't exist or isn't ready, render nothing.
  // Otherwise, render the children into the window's body using a portal.
  if (!pipWindow || !isReady || !pipWindow.document) {
    return null;
  }

  // For Document PiP windows, render into body
  const container = pipWindow.document.body;

  if (!container) {
    return null;
  }

  return createPortal(children, container);
};

export default DocumentPiP;
